import { useState } from 'react';
import { AlertCircle, Check, Pencil, X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import {
  BlueprintSpecSchema,
  validateBlueprintSpec,
  BlueprintValidationError,
  MATH_FUNCTION_IDS,
  type BlueprintSpec,
} from '@jeraisonne/db/blueprint';
import { useUpdateBlueprintSpec, type ExerciseBlueprintRow } from '@/lib/blueprint/api';

export type CheckSpecResult = { ok: true; spec: BlueprintSpec } | { ok: false; message: string };

/**
 * Revalide un texte JSON avec EXACTEMENT les mêmes règles que l'edge fn
 * `blueprint-build` après l'appel LLM (schéma Zod + fermeture anti-hallucination
 * `validateBlueprintSpec`) — jamais de spec invalide enregistrable. Exportée
 * pour être testée indépendamment du composant (pas de montage React requis).
 */
export function checkSpecText(text: string): CheckSpecResult {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (e) {
    return { ok: false, message: `JSON invalide : ${e instanceof Error ? e.message : String(e)}` };
  }
  const parsed = BlueprintSpecSchema.safeParse(json);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const message = first ? `${first.path.join('.')} : ${first.message}` : 'Spec invalide';
    return { ok: false, message };
  }
  try {
    validateBlueprintSpec(parsed.data, { candidateFunctionIds: MATH_FUNCTION_IDS });
  } catch (e) {
    if (e instanceof BlueprintValidationError) return { ok: false, message: e.message };
    throw e;
  }
  return { ok: true, spec: parsed.data };
}

/**
 * Éditeur de la spec d'un Blueprint — corrige ce que le LLM a mal segmenté
 * (paramètre mal borné, fonction incorrecte, contrainte manquante, template
 * qui fuite un résultat…) sans repasser par Supabase Studio. Le schéma
 * (paramètres/contraintes/templates/solve path/questions/visuel) est trop
 * riche pour un formulaire structuré complet en v1 — édition JSON directe,
 * mais avec la MÊME validation stricte que la génération avant tout
 * enregistrement (jamais de "enregistrer à l'aveugle").
 */
export function BlueprintSpecEditor({ blueprint }: { blueprint: ExerciseBlueprintRow }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'error' | 'success'; message: string } | null>(
    null,
  );
  const updateSpec = useUpdateBlueprintSpec();

  function startEditing() {
    setText(JSON.stringify(blueprint.spec, null, 2));
    setFeedback(null);
    setEditing(true);
  }

  function handleValidate() {
    const result = checkSpecText(text);
    setFeedback(
      result.ok
        ? { type: 'success', message: 'Spec valide.' }
        : { type: 'error', message: result.message },
    );
  }

  async function handleSave() {
    const result = checkSpecText(text);
    if (!result.ok) {
      setFeedback({ type: 'error', message: result.message });
      return;
    }
    try {
      await updateSpec.mutateAsync({ blueprint, spec: result.spec });
      setEditing(false);
    } catch (e) {
      setFeedback({ type: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }

  if (!editing) {
    return (
      <Button variant="outline" size="sm" onClick={startEditing}>
        <Pencil className="h-3.5 w-3.5" /> Modifier la spec
      </Button>
    );
  }

  return (
    <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-display text-ink-900 text-sm font-bold">Modifier la spec (JSON)</p>
        <button
          type="button"
          onClick={() => setEditing(false)}
          aria-label="Fermer l'éditeur"
          className="text-ink-400 hover:text-ink-700 flex h-9 w-9 shrink-0 items-center justify-center"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="text-ink-500 text-xs">
        Corrige directement le document (paramètres, contraintes, templates, solve path, questions,
        visuel). « Valider » vérifie la cohérence sans enregistrer — mêmes règles que la génération
        (dépendances en avant, fonctions connues, pas de fuite de résultat dans l&apos;énoncé).
        Enregistrer incrémente la version : reconstruis les blocs et régénère la banque ensuite.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
        rows={20}
        aria-label="Spec du Blueprint (JSON)"
        className="border-ink-200 text-ink-900 focus:border-primary-500 w-full rounded-xl border bg-white p-3 font-mono text-xs transition-colors focus:outline-none"
      />
      {feedback && (
        <div
          className={
            feedback.type === 'error'
              ? 'bg-error-50 border-error-100 text-error-700 flex items-start gap-2 rounded-xl border px-3 py-2 text-sm'
              : 'bg-xp-50 border-xp-100 text-xp-700 flex items-start gap-2 rounded-xl border px-3 py-2 text-sm'
          }
        >
          {feedback.type === 'error' ? (
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <Check className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={updateSpec.isPending}
          className="w-full sm:w-auto"
        >
          Annuler
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleValidate}
          disabled={updateSpec.isPending}
          className="w-full sm:w-auto"
        >
          <Check className="h-3.5 w-3.5" /> Valider
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void handleSave()}
          disabled={updateSpec.isPending}
          className="w-full sm:w-auto"
        >
          {updateSpec.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
}
